import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { FiUser, FiMail, FiPhone, FiBriefcase, FiEdit2, FiSave, FiX } from 'react-icons/fi';

const roleLabels = {
  superadmin: 'Super Admin',
  reseller: 'Reseller',
  customer: 'Merchant',
  branch_manager: 'Branch Manager',
  cashier: 'Cashier',
  staff: 'Staff',
};

export default function MyProfile() {
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ fullName: user?.fullName || '', phone: user?.phone || '' });

  const initials = user?.fullName
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

  const handleSave = async () => {
    if (!form.fullName.trim()) return toast.error('Full name is required');
    setLoading(true);
    try {
      const res = await authService.updateProfile(form);
      updateUser(res.data.data);
      toast.success('Profile updated successfully');
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({ fullName: user?.fullName || '', phone: user?.phone || '' });
    setEditing(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">My Profile</h1>
        <p className="text-sm text-gray-500 mt-0.5">View and update your account information</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Avatar header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-8 py-8 flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {initials}
          </div>
          <div>
            <p className="text-white font-semibold text-lg leading-tight">{user?.fullName}</p>
            <p className="text-purple-100 text-sm mt-0.5">{roleLabels[user?.role] || user?.role}</p>
            <p className="text-purple-200 text-xs mt-0.5">{user?.email}</p>
          </div>
        </div>

        {/* Info fields */}
        <div className="p-8 space-y-5">
          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <FiUser size={12} /> Full Name
            </label>
            {editing ? (
              <input
                type="text"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            ) : (
              <p className="text-sm text-gray-900 font-medium">{user?.fullName || '—'}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <FiMail size={12} /> Email Address
            </label>
            <p className="text-sm text-gray-900 font-medium">{user?.email || '—'}</p>
            <p className="text-xs text-gray-400 mt-0.5">Email cannot be changed</p>
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <FiPhone size={12} /> Phone Number
            </label>
            {editing ? (
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Enter phone number"
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            ) : (
              <p className="text-sm text-gray-900 font-medium">{user?.phone || '—'}</p>
            )}
          </div>

          {/* Role */}
          <div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              <FiBriefcase size={12} /> Role
            </label>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700">
              {roleLabels[user?.role] || user?.role}
            </span>
          </div>

          {/* Last login */}
          {user?.lastLogin && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Last Login
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {new Date(user.lastLogin).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          {editing ? (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <FiX size={15} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60"
              >
                <FiSave size={15} /> {loading ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <FiEdit2 size={15} /> Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
